import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {RICH_TEXT_APPEARANCE} from '@amblique/king-living/app/contentful/models/rich-text-appearance';

const helpers = createMultiStyleConfigHelpers(['root', 'markup', 'animationContainer']);

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "markup")[]
 * }>} RichTextBlockStyles
 */
export const richTextBlockStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.RichTextBlock} args.component - RichTextBlock component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {RichTextBlockStyles} - Component styles
   */
  baseStyle: ({
    component: {alignment = 'left', animation, quote, image},
  }) => {
    return {
      root: {
        textAlign: alignment,
      },
      markup: {
      },
      animationContainer: {
        sx: {
          // h1 => headingXL (target all descendant spans)
          'h1 span, .h1 span': {
            fontSize: {base: '2rem', lg: '2.5rem'},
            fontWeight: 500,
            lineHeight: 1.2,
            fontFamily: 'heading',
          },
          // h2 => headingL
          'h2 span, .h2 span': {
            fontSize: {base: '1.5rem', lg: '2rem'},
            fontWeight: 500,
            lineHeight: {base: 1.5, lg: 1.4},
            fontFamily: 'heading',
          },
          // h3 => labelXL
          'h3 span, .h3 span': {
            fontSize: {base: '1.25rem', lg: '1.5rem'},
            fontWeight: 500,
            lineHeight: {base: 1.5, lg: 1.4},
            fontFamily: 'body',
          },
          // h4–h6: match global .amb-markup rich-text matrix (same as blog body)
          'h4 span, .h4 span': {
            fontSize: {base: '1.125rem', lg: '1.25rem'},
            fontWeight: 500,
            lineHeight: 1.4,
            fontFamily: 'body',
          },
          'h5 span, .h5 span': {
            fontSize: {base: '1rem', lg: '1.125rem'},
            fontWeight: 500,
            lineHeight: 1.4,
            fontFamily: 'body',
          },
          'h6 span, .h6 span': {
            fontSize: {base: '0.875rem', lg: '1rem'},
            fontWeight: 600,
            lineHeight: 1.4,
            fontFamily: 'body',
          },
          // p => bodyDefault
          'p span': {
            fontSize: {base: '0.875rem', lg: '1rem'},
            fontWeight: 400,
            lineHeight: 1.4,
            fontFamily: 'body',
          },
        },
      },
    };
  },

  variants: {
    /**
     * CF-05: Animated Hero Copy Block – Intro typography, layout for copy below hero (Range page).
     * Desktop Figma 3132-2139: Intro – 32px, line-height 1.5, center, 839px, rgba(27,26,24,0.8).
     * Mobile Figma 3132-6336: 18/Body Reg – 18px, line-height 1.6, center, color #414141.
     * Spacing: desktop top 100px / bottom 170px; mobile top 70px / bottom 75px.
     */
    [RICH_TEXT_APPEARANCE.CF_05]: ({
      component: {quote, image},
    }) => ({
      root: {
        textAlign: 'center',
        maxWidth: {base: '100%', lg: '839px'},
        margin: '0 auto',
        paddingTop: {base: '70px', lg: '100px'},
        paddingBottom: {base: '75px', lg: '80px'},
        paddingLeft: {base: '34px', lg: '34px'},
        paddingRight: {base: '34px', lg: '34px'},
      },
      markup: {
        sx: {
          'p, .chakra-text': {
            fontSize: {base: '1.125rem', lg: '2rem'},
            fontWeight: 400,
            lineHeight: {base: 1.6, lg: 1.5},
            fontFamily: 'body',
            textAlign: 'center',
            color: {base: '#414141', lg: 'rgba(27, 26, 24, 0.8)'},
          },
        },
      },
      animationContainer: {
        sx: {
          // CF-05 Desktop 3132-2139: Intro 32px, 1.5, rgba(27,26,24,0.8). Mobile 3132-6336: 18/Body Reg 18px, 1.6, #414141
          // Target both block nodes and spans: fade-up parses HTML as <p>text</p> (no inner spans);
          // highlight-word wraps each word in <span>, so p span matches. Both need the same sizes.
          'p, .chakra-text, p span, .chakra-text span': {
            fontSize: {base: '1.125rem', lg: '2rem'},
            fontWeight: 400,
            lineHeight: {base: 1.6, lg: 1.5},
            fontFamily: 'body',
            textAlign: 'center',
            color: {base: '#414141', lg: 'rgba(27, 26, 24, 0.8)'},
          },
          'h1, .h1, h1 span, .h1 span': {
            fontSize: {base: '2rem', lg: '2.5rem'},
            fontWeight: 500,
            lineHeight: 1.2,
            fontFamily: 'heading',
          },
          'h2, .h2, h2 span, .h2 span': {
            fontSize: {base: '1.5rem', lg: '2rem'},
            fontWeight: 500,
            lineHeight: {base: 1.5, lg: 1.4},
            fontFamily: 'heading',
          },
        },
      },
    }),
  },
});
